import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const testimonials = [
  {
    id: 1,
    name: 'Dr Ripudaman Dawra',
    role: 'Parent of Aviyansh (Age 3)',
    content: 'Neev Play School has been a blessing for our family. Aviyansh has developed so much confidence and creativity since joining. The screen-free environment really makes a difference - he is more engaged and curious about the world around him.',
    rating: 5,
    avatar: '👩‍👦',
    category: 'Parent',
    childAge: '3 years',
    duration: '1.5 years'
  },
  {
    id: 2,
    name: 'Mr Jagdev Singh',
    role: 'Parents of Samrath (Age 3)',
    content: 'We were worried about finding the right playschool, but Neev exceeded our expectations. Samrath loves coming here every day. The teachers are amazing and the activities are so well thought out. He is learning while having fun!',
    rating: 5,
    avatar: '👨‍👩‍👧',
    category: 'Parent',
    childAge: '3 years',
    duration: '8 months'
  },
  {
    id: 4,
    name: 'Mr Gurdev Singh',
    role: 'Parents of Kaynaaz (Age 4)',
    content: 'Our daughter has been at Neev for 1 year now and the transformation is incredible. She is more independent, creative, and socially confident. The teachers understand each child\'s unique needs and nurture them accordingly.',
    rating: 5,
    avatar: '👨‍👩‍👧‍👦',
    category: 'Parent',
    childAge: '4 years',
    duration: '1 year'
  }
];

const Testimonials: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="testimonials" className="bg-gradient-to-br from-white to-brand-light py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-brand-purple mb-6">
            What <span className="text-brand-pink">Parents</span> Say
          </h2>
          <p className="text-lg text-brand-blue max-w-2xl mx-auto">
            Hear from our happy parents about their Neev experience
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <AnimatePresence mode="wait">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -50, scale: 0.9 }}
                transition={{ duration: 0.6, delay: idx * 0.1, ease: 'easeOut' }}
                className="group relative"
              >
                <div className="bg-white rounded-3xl p-16 shadow-bubble hover:shadow-brand transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-brand-yellow h-full">
                  {/* Rating Stars */}
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-brand-yellow text-xl">⭐</span>
                    ))}
                  </div>

                  {/* Testimonial Content */}
                  <div className="mb-6">
                    <p className="text-brand-dark leading-relaxed italic">
                      "{testimonial.content}"
                    </p>
                  </div>

                  {/* Author Info */}
                  <div className="flex items-center space-x-4">
                    <div className="text-4xl bg-brand-pink bg-opacity-10 p-3 rounded-2xl">
                      {testimonial.avatar}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-brand-dark group-hover:text-brand-pink transition-colors duration-300">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-brand-blue">{testimonial.role}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-brand-green">
                        <span>👶 {testimonial.childAge}</span>
                        <span>📅 {testimonial.duration}</span>
                      </div>
                    </div>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 right-16">
                    <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-brand-green bg-opacity-10 text-brand-green">
                      {testimonial.category}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Statistics Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12"
        >
          {[
            { number: '98%', label: 'Parent Satisfaction', icon: '😊' },
            { number: '100+', label: 'Happy Families', icon: '👨‍👩‍👧‍👦' },
            { number: '4.9', label: 'Average Rating', icon: '⭐' },
            { number: '44+', label: 'Years Experience', icon: '🎓' }
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, delay: 0.8 + idx * 0.1, ease: 'easeOut' }}
              className="text-center bg-white rounded-2xl p-6 shadow-bubble"
            >
              <div className="text-3xl mb-2">{stat.icon}</div>
              <div className="text-2xl font-bold text-brand-pink mb-1">{stat.number}</div>
              <div className="text-sm text-brand-dark font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        
      </div>
    </section>
  );
};

export default Testimonials; 